import { Leaf, BatteryCharging, Thermometer, Home, Hammer } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

export const Energy = () => {
    const t = useT();

    const FEATURES = [
        {
            icon: Leaf,
            title: t({ de: "Effizienzhaus 40", en: "Energy class 40" }),
            text: t({
                de: "Energieeffizienter Neubau im Standard EH 40 – moderne Fenster mit Dreifachverglasung, hochwertige Fassadengestaltung, moderne Gebäudetechnik.",
                en: "Energy-efficient new build at class EH 40 – modern triple-glazed windows, high-quality façade, state-of-the-art building services.",
            }),
        },
        {
            icon: Thermometer,
            title: t({ de: "Eigene Wärmepumpe", en: "Own heat pump" }),
            text: t({
                de: "Pro Wohnung eine eigene Luft-Luft-Wärmepumpe. Effiziente Heizung & Kühlung, klare Verbrauchszuordnung, niedrige Betriebskosten.",
                en: "One air-to-air heat pump per apartment. Efficient heating & cooling, clear consumption tracking, low running costs.",
            }),
        },
        {
            icon: BatteryCharging,
            title: t({ de: "PV-Anlage inklusive", en: "PV system included" }),
            text: t({
                de: "2 kW Photovoltaik-Anlage inkl. passendem Stromspeicher separat für jede Wohnung. Wallbox-Anschluss an jedem Stellplatz vorbereitet, Wallbox optional auf Wunsch.",
                en: "2 kW PV system with a dedicated matching battery storage per apartment. Wallbox connection prepared at every parking spot, wallbox optional on request.",
            }),
        },
        {
            icon: Home,
            title: t({ de: "Schlanke Gemeinschaft", en: "Lean community" }),
            text: t({
                de: "Wenig Gemeinschaftseigentum, kein Aufzug, wartungsarme Bauweise – für niedrige laufende Kosten und entspanntes Eigentum.",
                en: "Minimal shared property, no elevator, low-maintenance build – for low running costs and stress-free ownership.",
            }),
        },
        {
            icon: Hammer,
            title: t({ de: "Erweiterter Ausbau", en: "Extended fit-out" }),
            text: t({
                de: "Übergabe im erweiterten Ausbauzustand. Badezimmer gefliest. Bodenbeläge und Malerarbeiten individuell durch den Käufer.",
                en: "Handed over in extended fit-out condition. Bathrooms tiled. Flooring and painting are chosen and executed by the buyer.",
            }),
        },
    ];

    return (
        <section id="energie" className="bg-cream py-24 sm:py-32" data-testid="energy-section">
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
                    <div className="lg:col-span-7">
                        <span className="overline">{t({ de: "06 / 12 — Bauweise · Energie", en: "06 / 12 — Build · Energy" })}</span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "Modern. Effizient.", en: "Modern. Efficient." })}
                            <span className="italic text-brand-500"> {t({ de: "Wartungsarm.", en: "Low-maintenance." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="body-md">
                            {t({
                                de: "Die Bauweise ist bewusst durchdacht: hohe Effizienz, geringe Betriebskosten, klare Verbrauchszuordnung und optionale Eigenstromversorgung pro Wohnung.",
                                en: "The build is deliberately designed: high efficiency, low running costs, clear consumption tracking and optional own-power supply per apartment.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                    {FEATURES.map((f, i) => {
                        const Icon = f.icon;
                        const span = i === 0 ? "col-span-12 md:col-span-7 lg:col-span-7"
                            : i === 1 ? "col-span-12 md:col-span-5 lg:col-span-5"
                                : i === 2 ? "col-span-12 md:col-span-5 lg:col-span-4"
                                    : i === 3 ? "col-span-12 md:col-span-7 lg:col-span-4"
                                        : "col-span-12 lg:col-span-4";
                        return (
                            <div
                                key={f.title}
                                className={`${span} bg-cream p-8 lg:p-10 group transition-colors hover:bg-white`}
                                data-testid={`energy-feature-${i}`}
                            >
                                <div className="w-11 h-11 flex items-center justify-center bg-brand-500 text-white mb-5 group-hover:bg-ink transition-colors">
                                    <Icon size={20} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-heading text-xl text-ink mb-2">{f.title}</h3>
                                <p className="font-body text-sm text-stone-600 leading-relaxed">
                                    {f.text}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
